import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import get from 'lodash.get';
import has from 'lodash.has';
import { MODELS_COMPARE } from 'src/store/ActionTypes';
import CONSTANTS from 'src/store/constants';

import { all as allTruthy, any } from 'src/functions';
import { intersection } from 'src/functions/set';
import * as api from 'src/store/api';

const selectModels = (state) => state.modelsCompare.models;

const selectQuantiles = (state) => state.modelsCompare.quantiles;

const selectAvailableQuantiles = (state) => state.modelsCompare.availableQuantiles;

function* fetchModels(action) {
  try {
    const { payload: modelIds } = action;
    const models = yield all(modelIds.map((mdlId) => call(api.fetchModel, mdlId)));
    const isRoiCalcPage = window.location.pathname.includes('roi');
    if (isRoiCalcPage) {
      const segmentOptions = {
        headers: {
          'X-Fields': 'id,datasetId',
        },
      };
      const segments = yield all(
        models.map((model) => api.fetchSegment(model?.modelData?.dataPayload?.actuarial_segment_id, segmentOptions))
      );
      const datasetOptions = {
        headers: {
          'X-Fields': 'id,payload{roi_calculator_seed}',
        },
      };
      const datasets = yield all(segments.map((segment) => api.fetchDataset(segment.datasetId, datasetOptions)));
      yield put({
        type: MODELS_COMPARE.SET_ROI_CALC_SEEDS_FROM_DATASETS,
        payload: datasets?.map?.((dataset) => dataset?.payload?.roi_calculator_seed),
      });
    }
    yield put({
      type: MODELS_COMPARE.FETCH_MODELS_SUCCEEDED,
      payload: models,
    });
  } catch (e) {
    yield put({
      type: MODELS_COMPARE.FETCH_MODELS_FAILED,
    });
  }
}

function* fetchModelsSaga() {
  yield takeLatest(MODELS_COMPARE.FETCH_MODELS, fetchModels);
}

/**
 * extractQuantileInfo - extract quantile info from the models
 * @param {{type: string, payload: Object[] }} action
 * @return {Generator<*, void, *>}
 */
function* extractQuantileInfo(action) {
  try {
    const query = new URLSearchParams(window.location.search);
    const equal = query.get('equal') ?? 'persons';
    const models = action.payload;
    const modelTypes = Array(models.length);
    const isLegacyActuarial = Array(models.length);
    const perModelQuantiles = Array(models.length);
    const perModelLiftScores = [];
    models.forEach((mdl, idx) => {
      const modelVersion = mdl?.payload?.version;
      modelTypes[idx] = mdl.dataModelType;
      isLegacyActuarial[idx] = ['LIFT_VERSION_1_ACT', null, undefined].includes(modelVersion);
    });
    // NOTE: if true, then skip populating premiums and persons use current only
    const anyLegacy = any(isLegacyActuarial);
    // NOTE: if true, skip _processing_ persons as well
    const allLegacyOrAnyClassifier =
      allTruthy(isLegacyActuarial) || any(modelTypes.map((v) => v === CONSTANTS.TAL_MODEL.TAG.CLASSIFIER));
    const allModelsHavePremiums = allTruthy(
      models.map(
        (model) =>
          has(model, 'modelData.dataPayload.equal_premiums.lift_scores') ||
          has(model, 'modelData.dataPayload.equal_premium.lift_scores')
      )
    );
    const allModelsHavePersons = allTruthy(
      models.map(
        (model) =>
          has(model, 'modelData.dataPayload.equal_persons.lift_scores') ||
          has(model, 'modelData.dataPayload.equal_person.lift_scores')
      )
    );
    models.forEach((mdl, idx) => {
      perModelQuantiles[idx] = {};
      if (allLegacyOrAnyClassifier) {
        const liftScores = get(
          mdl,
          'modelData.dataPayload.lift_scores',
          get(mdl, 'modelData.dataPayload.liftScores', [])
        );
        perModelQuantiles[idx].current = new Set(liftScores.map((data) => data?.percentile?.length));
        perModelLiftScores[idx] = liftScores;
      } else if (anyLegacy) {
        const isLegacy = isLegacyActuarial[idx];
        const liftScores = isLegacy
          ? get(mdl, 'modelData.dataPayload.lift_scores', get(mdl, 'modelData.dataPayload.liftScores', []))
          : get(mdl, 'modelData.dataPayload.equal_persons.lift_scores', []);
        perModelQuantiles[idx].current = new Set(liftScores.map((data) => data?.percentile?.length));
        perModelLiftScores[idx] = liftScores;
      } else {
        const equalPersonsLiftScores = mdl?.modelData?.dataPayload?.equal_persons?.lift_scores;
        const equalPremiumsLiftScores =
          mdl?.modelData?.dataPayload?.equal_premiums?.lift_scores ??
          mdl?.modelData?.dataPayload?.equal_premium?.lift_scores;
        if (equalPremiumsLiftScores && allModelsHavePremiums)
          perModelQuantiles[idx].premiums = new Set(equalPremiumsLiftScores.map((data) => data?.percentile?.length));
        else delete perModelQuantiles[idx].premiums;
        perModelQuantiles[idx].persons = new Set(equalPersonsLiftScores.map((data) => data?.percentile?.length));
        perModelQuantiles[idx].current = new Set(perModelQuantiles[idx][equal] ?? perModelQuantiles[idx].persons);
        if (equal === 'premiums') perModelLiftScores[idx] = equalPremiumsLiftScores;
        else perModelLiftScores[idx] = equalPersonsLiftScores;
      }
    });
    // NOTE: now we get the intersection of all the sets for safe quantiles to switch to
    const availableQuantiles = {};
    availableQuantiles.current = [
      ...perModelQuantiles.slice(1).reduce((acc, cur) => intersection(acc, cur.current), perModelQuantiles[0].current),
    ];
    if (allModelsHavePersons)
      availableQuantiles.persons = [
        ...perModelQuantiles
          .slice(1)
          .reduce((acc, cur) => intersection(acc, cur.persons), perModelQuantiles[0].persons),
      ];
    if (allModelsHavePremiums)
      availableQuantiles.premiums = [
        ...perModelQuantiles
          .slice(1)
          .reduce((acc, cur) => intersection(acc, cur.premiums), perModelQuantiles[0].premiums),
      ];
    const defaultQuantiles = CONSTANTS.TAL_MODEL.DEFAULT_QUANTILE.DEFAULT; // NOTE: As of PDAE-5678, 5
    yield put({
      type: MODELS_COMPARE.SET_AVAILABLE_QUANTILES,
      payload: availableQuantiles,
    });
    yield put({
      type: MODELS_COMPARE.SET_QUANTILES,
      payload: defaultQuantiles,
    });
    yield put({
      type: MODELS_COMPARE.SET_QUANTILE_INDICES,
      payload: perModelLiftScores
        .map((liftScores) => liftScores.map((liftScore) => liftScore?.percentile?.length))
        .map((lengths) => lengths.findIndex((len) => len === defaultQuantiles)),
    });
    yield put({
      type: MODELS_COMPARE.SET_LIFT_SCORES,
      payload: perModelLiftScores,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

/**
 * updateQuantileInfo - update quantile info from the chart
 * @param {{type: string, payload: Object }} action
 * @return {Generator<*, void, *>}
 */
function* updateQuantileInfo(action) {
  try {
    const nextEqualType = action.payload;
    const availableQuantiles = yield select(selectAvailableQuantiles);
    const quantiles = yield select(selectQuantiles);
    const models = yield select(selectModels);
    // NOTE: Do not update until available quantiles and the current quantile selection is defined
    if (!quantiles || !availableQuantiles?.current?.length) return;
    const modelTypes = Array(models.length);
    const isLegacyActuarial = Array(models.length);
    const nextAvailableQuantiles = {
      ...availableQuantiles,
      current: [...availableQuantiles[nextEqualType]],
    };
    const quantileIdx = nextAvailableQuantiles[nextEqualType].findIndex((qn) => qn === quantiles);
    const defaultQuantiles = CONSTANTS.TAL_MODEL.DEFAULT_QUANTILE.DEFAULT; // NOTE: As of PDAE-5678, 5
    const updateQuantiles = quantileIdx === -1;
    const perModelLiftScores = [];
    models.forEach((mdl, idx) => {
      const modelVersion = mdl?.payload?.version;
      modelTypes[idx] = mdl.dataModelType;
      isLegacyActuarial[idx] = ['LIFT_VERSION_1_ACT', null, undefined].includes(modelVersion);
    });
    // NOTE: if true, then skip populating premiums and persons use current only
    const anyLegacy = any(isLegacyActuarial);
    // NOTE: if true, skip _processing_ persons as well
    const allLegacyOrAnyClassifier =
      allTruthy(isLegacyActuarial) || any(modelTypes.map((v) => v === CONSTANTS.TAL_MODEL.TAG.CLASSIFIER));
    models.forEach((mdl, idx) => {
      if (allLegacyOrAnyClassifier) {
        perModelLiftScores[idx] = get(
          mdl,
          'modelData.dataPayload.lift_scores',
          get(mdl, 'modelData.dataPayload.liftScores', [])
        );
      } else if (anyLegacy) {
        const isLegacy = isLegacyActuarial[idx];
        perModelLiftScores[idx] = isLegacy
          ? get(mdl, 'modelData.dataPayload.lift_scores', get(mdl, 'modelData.dataPayload.liftScores', []))
          : get(mdl, 'modelData.dataPayload.equal_persons.lift_scores', []);
      } else {
        const query = new URLSearchParams(window.location.search);
        const equal = query.get('equal') ?? 'persons';
        const equalPersonsLiftScores = mdl?.modelData?.dataPayload?.equal_persons?.lift_scores;
        const equalPremiumsLiftScores =
          mdl?.modelData?.dataPayload?.equal_premiums?.lift_scores ??
          mdl?.modelData?.dataPayload?.equal_premium?.lift_scores;
        if (equal === 'premiums') perModelLiftScores[idx] = equalPremiumsLiftScores;
        else perModelLiftScores[idx] = equalPersonsLiftScores;
      }
    });
    yield put({
      type: MODELS_COMPARE.SET_LIFT_SCORES,
      payload: perModelLiftScores,
    });
    yield put({
      type: MODELS_COMPARE.SET_AVAILABLE_QUANTILES,
      payload: nextAvailableQuantiles,
    });
    if (updateQuantiles) {
      yield put({
        type: MODELS_COMPARE.SET_QUANTILES,
        payload: defaultQuantiles,
      });
      yield put({
        type: MODELS_COMPARE.SET_QUANTILE_INDICES,
        payload: perModelLiftScores
          .map((liftScores) => liftScores.map((liftScore) => liftScore?.percentile?.length))
          .map((lengths) => lengths.findIndex((len) => len === defaultQuantiles)),
      });
    } else {
      yield put({
        type: MODELS_COMPARE.SET_QUANTILE_INDICES,
        payload: perModelLiftScores
          .map((liftScores) => liftScores.map((liftScore) => liftScore?.percentile?.length))
          .map((lengths) => lengths.findIndex((len) => len === quantiles)),
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* extractQuantileInfoSaga() {
  yield takeLatest(MODELS_COMPARE.FETCH_MODELS_SUCCEEDED, extractQuantileInfo);
}

function* updateQuantileInfoSaga() {
  yield takeLatest(MODELS_COMPARE.SET_EQUAL_TYPE, updateQuantileInfo);
}

export default [extractQuantileInfoSaga(), updateQuantileInfoSaga(), fetchModelsSaga()];
